// TODO: There are a few any in this file, let's try to type them
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * This module exports Scalyr-defined TypeScript types for use in TypeScript modules.
 */

import { ElementType, MouseEventHandler, ReactNode } from 'react';
import { Dictionary } from 'lodash';

import * as K from 'common/constants';
import { DynamicTestId } from 'common/element';
import { Permission, Theme } from 'common/graphqlTypes';
import { DATASOURCE, FIELD_TYPE } from 'schemas/types';

// We extend the global Window type to include properties attached on the server.
// See https://github.com/scalyr/scalyr/blob/master/ScalyrSite/src/com/scalyr/server/DefaultServlet.java#L532
declare global {
  interface Window {
    pendo: any;
    xsrfToken: string;
    reactPaths: Array<string>;
    reactPathsBlacklist: Array<string>;
    BUILD_INFO: {
      buildHash: string;
      buildDate: string;
    };
    MktoForms2: any;
  }
}

export enum PERMISSION_REQUIRING_FEATURE {
  ALERTS           = 'alerts',
  DASHBOARDS       = 'dashboards',
  LABS             = 'labs',
  LOGS             = 'logs',
  PRIVATE_SEARCHES = 'privateSearches',
  SEARCH           = 'search',
  SHARED_SEARCHES  = 'sharedSearches',
}

export type TimeRange = {
  startTime: string;
  endTime?: string;
};

export type UrlParams = Record<K.URL_PARAM, string>;

export type UrlParamSet = {
  eventParamList: Array<K.URL_PARAM>;
  viewParamList: Array<K.URL_PARAM>;
  disposableParamList: Array<K.URL_PARAM>;
};

export type Point = {
  x: number;
  y: number;
};

export type Range = {
  min: number;
  max: number;
};

// #region Tranforms - types used for transforming content in search results

export enum TRANSFORM_TYPE {
  PRETTY_PRINT = 'pretty-print',
  HIGHLIGHT =    'highlight',
  LINKIFY =      'linkify',
}

export type LinkConfigSub = {
  arg:     number;
  match:   string;
  replace: string;
};

// structure of "searchResultLinks" entry in /scalyr/logs; see product help for SmartLinks and below
export type LinkConfig = {
  regex:          string;
  url:            string;
  searchFields?:  Array<string> | '*';
  subs?:          Array<LinkConfigSub>;
  linkTextArg?:   number;
  caseSensitive?: boolean;
};

export type MarkupRange = {
  min:      number;
  max:      number;
  startTag: string;
  endTag:   string;
  priority: number;
};

export type TransformArgs = {
  filter?:     string;
  linkConfig?: LinkConfig[];
};

// #endregion

/**
 * DimensionsObject is returned by useDimensions() hook from react-use-dimensions library.
 * Library doesn't have types exported
 */
export type DimensionsObject = {
  width: number;
  height: number;
  top: number;
  left: number;
  x: number;
  y: number;
  right: number;
  bottom: number;
};

export type HoverAction = {
  icon:    ReactNode;
  action:  (item: MenuItemConfig) => void;
  testId?: string;
};

// Bootstrap navbar variants
export enum NavbarVariant {
  DARK  = 'dark',
  LIGHT = 'light',
}

export enum AvailableTheme {
  SENTINEL_ONE      = Theme.sentinel_one,
  SENTINEL_ONE_DARK = Theme.sentinel_one_dark,
  SINGULARITY       = Theme.singularity,
  SINGULARITY_DARK  = Theme.singularity_dark,
}

// #region Components
/**
 * Configuration object for dropdown menu items.
 *
 * @prop active         true if this item is the currently active one
 * @prop as             element or component to use to render the item
 * @prop children       content of the menu item
 * @prop className      className(s) to add to the item
 * @prop clickHandler   function to call if item is clicked
 * @prop enterHandler   function to call when the mouse enters the item
 * @prop leaveHandler   function to call when the mouse leaves the item
 * @prop focused        if true, this item is focused (may not equate to browser focus)
 * @prop href           link target if item is clicked
 * @prop key            key (as required by React)
 * @prop text           text content of the item
 * @prop openInNewTab   if true, clicking item opens target in new tab
 * @prop hoverActions   list of icon/action pairs tied to hover
 * @prop item           object associated with this menu item
 * @prop testId         uniquely identifying string to use for testing
 * @prop disabled       if true, user interaction with the the item is disabled
 * @prop subMenuItems   array of menu item configs to be used in the sub-menu
 * @prop permission     {@see Permission} required; lower permissions will not see the item
 */
export type MenuItemConfig = {
  active?:                    boolean;
  as?:                        ElementType<any> | string;
  children?:                  ReactNode;
  className?:                 string;
  clickHandler?:              MouseEventHandler;
  enterHandler?:              MouseEventHandler;
  leaveHandler?:              MouseEventHandler;
  focused?:                   boolean;
  href?:                      string;
  key?:                       string;
  text?:                      string;
  openInNewTab?:              boolean;
  hoverActions?:              HoverAction[];
  item?:                      any;
  testId?:                    DynamicTestId;
  disabled?:                  boolean;
  subMenuItems?:              MenuItemConfig[];
  permission?:                Permission;
  disabledForSingularityXDR?: boolean;
  tooltip?:                   string;
  disabledTooltip?:           string;
};

// #endregion

// #region Log Overview page
export type LogOrSourceCount = {
  total: number;
  included: number;
};

export type Source = {
  name: string;
  logs: Array<number>;
};

export type ClusterController = {
  kind: string;
  name: string;
  podCount: number;
  labels: Array<string>;
};

export type ClusterDashboard = {
  urlTemplate: string;
  title: string;
};

export type Cluster = {
  cluster: string;
  controllers: Array<ClusterController>;
  dashboard: ClusterDashboard;
};

export type ClusterSources = {
  clusters: Array<Cluster>;
};

export type SPMOverviewMetrics = {
  errorRate: number;
  latencyAvg: number;
  trafficRate: number;
};

export type SPMOperationData = {
  errorsData: Array<number>;
  latencyData: Array<number>;
  metrics: SPMOverviewMetrics;
  trafficData: Array<number>;
};

// #endregion

// #region Sources dropdown

export type SourceAndLogDataSet = {
  all:                                Array<string>;
  sorted:                             Array<number>;
  containedBy?:                       Record<string, Array<number>>;
  nameToIndex?:                       Dictionary<string>;
  controllerNameToClusterNameToKind?: Record<string, Record<string, string>>;
};

export type SourceAndLogData = {
  [key in K.SEARCH_FIELD_DATA_TYPE]: SourceAndLogDataSet;
};

// #endregion

// #region Facets

export type FacetCompletionInfo = {
  values:    string[];
  type:      FIELD_TYPE;
  source?:   DATASOURCE;
};

export type FacetsCompletionInfo = Record<string, FacetCompletionInfo>;

// #endregion

// #region Search page

export type ViewToolbarProps = {
  timeRangeText:  string;
  timeZoneOffset: string;
  matchCountText: string;
};

export enum FETCH_ORIGIN {
  VALUE      = 'value',
  ATTRIBUTES = 'attributes',
}

export type LogToFetch = {
  continuationToken: string;       // log to fetch
  origin:            FETCH_ORIGIN; // why we're fetching
  field?:            string;       // field, if fetching complete value
};

// types for the values within an event
export type LogEntryKey = string;
export type LogEntryValue = string | number | boolean;
export type LogEntryKeyValues = Record<LogEntryKey, LogEntryValue>;

// node used to display a tree of key/value pairs; leaf nodes have values
export type DataNodeKey = LogEntryKey | Element | ReactNode;
export type DataNodeValue = LogEntryValue | Element | ReactNode;
export type DataNode = {
  key:       DataNodeKey;   // the last part of a dotted string
  fullKey?:  DataNodeKey;   // the entire dotted string
  value?:    DataNodeValue;
  children?: DataNode[];
};

// #endregion

// #region Graphs

export type PlotModel = {
  name: string;
  data: Array<[number | undefined, number | undefined]>;
  visible: boolean;
  color: string;
  pieData?: number;
  value?: string | null;
  op?: string;
  counts?: Array<number>;
  maxValue?: number;
};

export type HoverData = {
  time?: string;
  value?: string;
};

export type ChartSelectionRange = {
  start?: number;
  end?: number;
  selectionBox?: boolean;
  leftPosition?: number;
  sum?: number;
};

export type ParsedFacetExpression = {
  facetFunction?: string;
  facetVariable?: string;
};

// #endregion

// #region Tracing

export type Span = {
  spanId: string;
  startTime: number;
  endTime: number;
  serverInfo?: Record<string, string>;
  operation?: string;
  component?: string;
  service?: string;
  tags?: Record<string, string>;
  logs?: Array<SpanLogs>;
  level: number;
  children?: Array<Span>;
  next?: Span;
};

export type SpanLogs = {
  timestamp: number;
  fields: Record<string, string>;
};

export interface HoneyCombChartDataType {[key: string]: string | number}
// #endregion

// #region Graphs

export type GraphType = 'line' | 'area' | 'bar';

// #endregion

// #region Settings

export type Settings = Record<K.SETTING, any>;

// TODO: proper typing; boolean assignment runs into TS issue: https://github.com/microsoft/TypeScript/issues/31663
/*
export type Settings = {
  [K.SETTING.ADDITIONAL_FIELDS]:         Array<string>;
  [K.SETTING.AUTO_UPDATE_SEARCH]:        boolean;
  [K.SETTING.DEFAULT_START_TIME]:        string;
  [K.SETTING.DISPLAY_MODE]:              K.DISPLAY_MODE;
  [K.SETTING.ENABLE_HIDDEN_API_KEYS]:    boolean;
  [K.SETTING.LOG_VIEW_WINDOW]:           number;
  [K.SETTING.OVERVIEW_AGE_LIMIT]:        number;
  [K.SETTING.OVERVIEW_AGE_LIMIT_ALL]:    number;
  [K.SETTING.OVERVIEW_LOGS_PER_HOST]:    number;
  [K.SETTING.SHOW_SIDEBAR]:              boolean;
  [K.SETTING.SMART_LINKS_CONFIG]:        any;
  [K.SETTING.STRIPE_PUBLISHABLE_KEY]:    string;
  [K.SETTING.SYSTEM_FIELDS]:             Array<K.SYSTEM_FIELD>;
  [K.SETTING.THEME]:                     string;
  [K.SETTING.UNESCAPE_LOG_LINE_MESSAGE]: boolean;
};
*/

// #endregion

export type ViewSettings = {
  [K.VIEW_SETTING.DISABLE_LIVE_TAIL]: boolean;
  [K.VIEW_SETTING.SEARCH_ALL_TEAMS_BY_DEFAULT]: boolean;
};

// #region Preferences

type HomepagePreferences = { team: string; path: string };

export type Preferences = {
  [K.PREFERENCE.TIMEZONE]: string | null;
  [K.PREFERENCE.HOMEPAGES]: Array<HomepagePreferences>;
};

// #endRegion

// #region Logo

export type LogoInfo = {
  logoUrl: string;
  logoWidth: string
  favIcon: string;
};

// #endRegion
